import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Visibility as ViewIcon,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import { message, Modal } from "antd";
import {
  API_BASE_URL,
  getAuthHeaders,
  getUploadAuthHeaders,
  isAdminRole,
} from "../API/Api";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatPhone = (countryCode, phone) => {
  const parts = [countryCode, phone].filter(Boolean);
  return parts.join(" ").trim();
};

const normalizeUser = (item, index) => ({
  index: index + 1,
  id: item.id,
  name: item.name || "",
  email: item.email || "",
  role: item.role || "",
  country_code: item.country_code || "",
  phone: item.phone || "",
  company: item.company || "",
  county: item.county || "",
  balance: item.balance != null ? String(item.balance) : "",
  created_at: item.created_at || "",
  updated_at: item.updated_at || "",
  raw: item,
});

const getUserDetailFields = (user) =>
  [
    { label: "Name", value: user.name },
    { label: "Email", value: user.email },
    { label: "Role", value: user.role },
    { label: "Company", value: user.company },
    { label: "County", value: user.county },
    {
      label: "Phone",
      value: formatPhone(user.country_code, user.phone),
    },

    { label: "Last Updated", value: formatDate(user.updated_at) },
  ].filter((field) => field.value != null && field.value !== "");

const getUserFiles = (user) => (Array.isArray(user?.files) ? user.files : []);

const getRoleColor = (role) => {
  const value = String(role || "").toLowerCase();
  if (value === "admin") return "error";
  if (value === "user") return "primary";
  return "default";
};

const getApiErrorMessage = (errorData, fallback) => {
  if (errorData?.message) return errorData.message;
  const errors = errorData?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return fallback;
};

const GetUsers = () => {
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [profileFile, setProfileFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [savingBalanceId, setSavingBalanceId] = useState(null);
  const [balanceDrafts, setBalanceDrafts] = useState({});
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    getApiUsers();
  }, []);

  const getApiUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      const responseData = await res.json();
      const users = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

      const customerUsers = users.filter((item) => !isAdminRole(item.role));
      setDataSource(customerUsers.map(normalizeUser));
    } catch (error) {
      console.error("Error fetching users:", error);
      setDataSource([]);
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    const res = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      headers: getAuthHeaders(),
    });
    const responseData = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        getApiErrorMessage(responseData, `HTTP error! Status: ${res.status}`),
      );
    }

    return responseData?.data ?? responseData;
  };

  const openUserDetails = async (user) => {
    if (!user?.id) return;

    setSelectedUser(user);
    setProfileFile(null);
    setFileInputKey((prev) => prev + 1);
    setLoadingUserDetails(true);

    try {
      const userDetails = await fetchUserDetails(user.id);
      setSelectedUser(userDetails);
      if (userDetails.balance != null) {
        setDataSource((prev) =>
          prev.map((item) =>
            item.id === user.id
              ? normalizeUser(
                  { ...item.raw, balance: userDetails.balance },
                  item.index - 1,
                )
              : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      message.error(error.message || "Failed to load user details");
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
    setProfileFile(null);
    setLoadingUserDetails(false);
  };

  const getBalanceDraft = (row) => balanceDrafts[row.id] ?? row.balance ?? "";

  const handleBalanceDraftChange = (userId, value) => {
    setBalanceDrafts((prev) => ({ ...prev, [userId]: value }));
  };

  const handleSaveBalance = async (userId) => {
    const row = dataSource.find((item) => item.id === userId);
    const balanceValue = String(getBalanceDraft(row)).trim();

    if (balanceValue === "" || Number.isNaN(Number(balanceValue))) {
      message.warning("Please enter a valid balance");
      return;
    }

    if (row && balanceValue === String(row.balance ?? "")) {
      return;
    }

    setSavingBalanceId(userId);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ balance: balanceValue }),
      });

      const responseData = await res.json().catch(() => ({}));

      if (!res.ok) {
        const validationError = responseData?.errors?.balance?.[0];
        throw new Error(
          validationError ||
            getApiErrorMessage(
              responseData,
              `HTTP error! Status: ${res.status}`,
            ),
        );
      }

      const updatedUser = responseData?.data ?? { balance: balanceValue };
      setDataSource((prev) =>
        prev.map((item) =>
          item.id === userId
            ? normalizeUser(
                {
                  ...item.raw,
                  balance: updatedUser.balance ?? balanceValue,
                },
                item.index - 1,
              )
            : item,
        ),
      );
      setBalanceDrafts((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      message.success("Balance updated");
    } catch (error) {
      console.error("Error saving balance:", error);
      message.error(error.message || "Failed to update balance");
    } finally {
      setSavingBalanceId(null);
    }
  };

  const handleDeleteUserFile = (fileId) => {
    if (!selectedUser?.id) return;

    Modal.confirm({
      title: "Delete File",
      content: "Are you sure you want to delete this file?",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      async onOk() {
        setDeletingFileId(fileId);
        try {
          const res = await fetch(
            `${API_BASE_URL}/admin/users/${selectedUser.id}/files/${fileId}`,
            {
              method: "DELETE",
              headers: getAuthHeaders(),
            },
          );

          const responseData = await res.json().catch(() => ({}));

          if (!res.ok) {
            throw new Error(
              getApiErrorMessage(
                responseData,
                `HTTP error! Status: ${res.status}`,
              ),
            );
          }

          const userDetails = await fetchUserDetails(selectedUser.id);
          setSelectedUser(userDetails);
          message.success("File deleted successfully");
        } catch (error) {
          console.error("Error deleting user file:", error);
          message.error(error.message || "Failed to delete file");
          throw error;
        } finally {
          setDeletingFileId(null);
        }
      },
    });
  };

  const handleUploadProfileFile = async () => {
    if (!selectedUser?.id) return;

    if (!profileFile) {
      message.warning("Please select a file to upload");
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", profileFile);

      const res = await fetch(
        `${API_BASE_URL}/admin/users/${selectedUser.id}/files`,
        {
          method: "POST",
          headers: getUploadAuthHeaders(),
          body: formData,
        },
      );

      const responseData = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          getApiErrorMessage(responseData, `HTTP error! Status: ${res.status}`),
        );
      }

      const userDetails = await fetchUserDetails(selectedUser.id);
      setSelectedUser(userDetails);
      setProfileFile(null);
      setFileInputKey((prev) => prev + 1);
      message.success("File uploaded successfully");
    } catch (error) {
      console.error("Error uploading user file:", error);
      message.error(error.message || "Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };

  const columns = [
    { field: "index", headerName: "#", width: 70 },
    { field: "name", headerName: "Name", width: 180 },
    { field: "email", headerName: "Email", width: 220 },
    {
      field: "role",
      headerName: "Role",
      width: 110,
      renderCell: (params) =>
        params.row.role ? (
          <Chip
            label={params.row.role}
            size="small"
            color={getRoleColor(params.row.role)}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 160,
      valueGetter: (params) =>
        formatPhone(params.row.country_code, params.row.phone),
    },
    { field: "company", headerName: "Company", width: 150 },
    { field: "county", headerName: "County", width: 120 },
    {
      field: "balance",
      headerName: "Balance",
      width: 190,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TextField
            size="small"
            type="number"
            value={getBalanceDraft(params.row)}
            onChange={(e) =>
              handleBalanceDraftChange(params.row.id, e.target.value)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveBalance(params.row.id);
              }
            }}
            disabled={savingBalanceId === params.row.id}
            inputProps={{ min: 0, step: "0.01" }}
            sx={{ width: 110 }}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={savingBalanceId === params.row.id}
            onClick={() => handleSaveBalance(params.row.id)}
          >
            {savingBalanceId === params.row.id ? "..." : "Save"}
          </Button>
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          startIcon={<ViewIcon />}
          onClick={() => openUserDetails(params.row.raw)}
          sx={{
            backgroundColor: "#3A6F43",
            color: "#fff",
            minWidth: 40,
            px: 1.5,
            "&:hover": { backgroundColor: "#3A6F43" },
          }}
        />
      ),
    },
  ];

  const filteredRows = dataSource.filter((row) => {
    const term = searchText.toLowerCase();
    if (!term) return true;
    const { raw, ...searchable } = row;
    return Object.values(searchable).some((val) =>
      String(val).toLowerCase().includes(term),
    );
  });

  const start = paginationModel.page * paginationModel.pageSize;
  const end = start + paginationModel.pageSize;
  const pagedRows = filteredRows.slice(start, end);
  const gridHeight = Math.max(220, pagedRows.length * 52 + 56);
  const detailFields = selectedUser ? getUserDetailFields(selectedUser) : [];
  const userFiles = selectedUser ? getUserFiles(selectedUser) : [];

  const renderUserFiles = (files) => (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Files ({files.length})
      </Typography>
      {files.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No files uploaded yet.
        </Typography>
      ) : (
        files.map((file) => (
          <Box
            key={file.id}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 1.5,
              mb: 1,
              backgroundColor: "#fafafa",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2">
                {file.url ? (
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name}
                  </a>
                ) : (
                  file.name
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {file.extension ? `.${file.extension}` : ""}
                {file.size != null
                  ? ` · ${Math.round(file.size / 1024)} KB`
                  : ""}
                {file.created_at ? ` · ${formatDate(file.created_at)}` : ""}
              </Typography>
            </Box>
            <IconButton
              color="error"
              size="small"
              disabled={deletingFileId === file.id}
              onClick={() => handleDeleteUserFile(file.id)}
              aria-label="Delete file"
            >
              {deletingFileId === file.id ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        ))
      )}
    </Box>
  );

  return (
    <div style={{ margin: "25px", overflow: "hidden" }}>
      <Box
        sx={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search Users"
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 250,
            maxWidth: 400,
            backgroundColor: "#f5f5f5",
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Users...
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            mb: 10,
            p: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Rows per page:
              </Typography>
              <Select
                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: e.target.value,
                    page: 0,
                  }))
                }
                size="small"
                sx={{ height: "36px", minWidth: "80px" }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Typography variant="body2">
              {`${start + 1}-${Math.min(end, filteredRows.length)} of ${
                filteredRows.length
              }`}
            </Typography>

            <Box>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.max(prev.page - 1, 0),
                  }))
                }
                disabled={paginationModel.page === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
                disabled={
                  (paginationModel.page + 1) * paginationModel.pageSize >=
                  filteredRows.length
                }
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          <DataGrid
            rows={pagedRows}
            columns={columns}
            disableSelectionOnClick
            hideFooter
            disableColumnResize
            sx={{
              height: gridHeight,
              minWidth: 1320,
              border: 0,
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
            }}
          />
        </Paper>
      )}

      <Dialog
        open={!!selectedUser}
        onClose={closeUserDetails}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedUser?.name ? selectedUser.name : "User Details"}
        </DialogTitle>
        <DialogContent dividers>
          {loadingUserDetails ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading user details...
              </Typography>
            </Box>
          ) : (
            selectedUser && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {detailFields.map((field) => (
                  <Box key={field.label}>
                    <Typography variant="caption" color="text.secondary">
                      {field.label}
                    </Typography>
                    {field.label === "Role" ? (
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={field.value}
                          size="small"
                          color={getRoleColor(field.value)}
                        />
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {field.value}
                      </Typography>
                    )}
                  </Box>
                ))}
                {detailFields.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No details available.
                  </Typography>
                )}

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Upload Profile File
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={uploadingFile}
                    >
                      {profileFile ? profileFile.name : "Select File"}
                      <input
                        key={fileInputKey}
                        type="file"
                        hidden
                        accept=".pdf,.xlsx,.xls,image/*"
                        onChange={(e) =>
                          setProfileFile(e.target.files?.[0] || null)
                        }
                      />
                    </Button>
                    {profileFile && (
                      <Button
                        size="small"
                        color="inherit"
                        disabled={uploadingFile}
                        onClick={() => {
                          setProfileFile(null);
                          setFileInputKey((prev) => prev + 1);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      disabled={uploadingFile || !profileFile}
                      onClick={handleUploadProfileFile}
                      sx={{
                        backgroundColor: "#3A6F43",
                        "&:hover": { backgroundColor: "#2f5a36" },
                      }}
                    >
                      {uploadingFile ? "Uploading..." : "Upload"}
                    </Button>
                  </Box>
                </Box>

                {renderUserFiles(userFiles)}
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeUserDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GetUsers;
